import React from 'react';
import { Box, Typography, Button, TextField, Divider } from '@mui/material';
import { Send, Chat } from '@mui/icons-material';
import { styled } from '@mui/material/styles';

const ChatContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  backgroundColor: 'rgba(0, 0, 0, 0.6)',
  backdropFilter: 'blur(20px)',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  borderRadius: '20px',
  padding: theme.spacing(2),
  position: 'relative',
  boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
  color: 'white'
}));

const MessagesContainer = styled(Box)(({ theme }) => ({
  flex: 1,
  overflowY: 'auto',
  marginBottom: theme.spacing(2),
  backgroundColor: 'rgba(255, 255, 255, 0.02)',
  borderRadius: '16px',
  padding: theme.spacing(1),
  border: '1px solid rgba(255, 255, 255, 0.1)',
  '&::-webkit-scrollbar': {
    width: '6px',
  },
  '&::-webkit-scrollbar-track': {
    background: 'rgba(255, 255, 255, 0.1)',
    borderRadius: '3px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: 'rgba(255, 255, 255, 0.3)',
    borderRadius: '3px',
    '&:hover': {
      background: 'rgba(255, 255, 255, 0.4)',
    },
  },
}));

const MessageBubble = styled(Box)(({ theme, isOwn }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: isOwn ? 'flex-end' : 'flex-start',
  marginBottom: theme.spacing(1),
  '&:last-child': {
    marginBottom: 0,
  }
}));

const MessageContent = styled(Box)(({ theme, isOwn }) => ({
  backgroundColor: isOwn ? 'rgba(103, 126, 234, 0.8)' : 'rgba(255, 255, 255, 0.1)',
  color: 'white',
  padding: theme.spacing(1, 1.5),
  borderRadius: isOwn ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
  maxWidth: '80%',
  wordBreak: 'break-word',
  border: `1px solid ${isOwn ? 'rgba(103, 126, 234, 0.3)' : 'rgba(255, 255, 255, 0.1)'}`,
  backdropFilter: 'blur(10px)',
  boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
  transition: 'all 0.2s ease',
  '&:hover': {
    transform: 'translateY(-1px)',
    boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
  }
}));

const MessageSender = styled(Typography)(({ theme, isOwn }) => ({
  fontSize: '0.75rem',
  color: 'rgba(255, 255, 255, 0.7)',
  marginBottom: theme.spacing(0.5),
  fontWeight: 500,
  textAlign: isOwn ? 'right' : 'left',
}));

const InputContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1),
  alignItems: 'center',
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  flex: 1,
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderRadius: '24px',
    color: 'white',
    fontSize: '0.875rem',
    backdropFilter: 'blur(10px)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    '& fieldset': {
      border: 'none',
    },
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.15)',
    },
    '&.Mui-focused': {
      backgroundColor: 'rgba(255, 255, 255, 0.15)',
      border: '1px solid rgba(103, 126, 234, 0.5)',
    },
  },
  '& .MuiOutlinedInput-input': {
    padding: '12px 16px',
    '&::placeholder': {
      color: 'rgba(255, 255, 255, 0.6)',
      opacity: 1,
    },
  },
}));

const SendButton = styled(Button)(({ theme }) => ({
  minWidth: '48px',
  width: '48px',
  height: '48px',
  borderRadius: '50%',
  backgroundColor: 'rgba(103, 126, 234, 0.8)',
  color: 'white',
  border: '1px solid rgba(103, 126, 234, 0.9)',
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: 'rgba(103, 126, 234, 1)',
    transform: 'scale(1.1)',
    boxShadow: '0 4px 20px rgba(103, 126, 234, 0.4)',
  },
  '&:disabled': {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    color: 'rgba(255, 255, 255, 0.5)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
  },
}));

const ChatInterface = ({
  chatMessages,
  chatInput,
  setChatInput,
  sendChatMessage,
  handleChatKeyPress,
  isConnected,
  hasHungUp
}) => {
  if (!isConnected || hasHungUp) {
    return null;
  }

  return (
    <ChatContainer>
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 1, 
        mb: 2,
        pb: 1,
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
      }}>
        <Chat sx={{ fontSize: '1.2rem', color: 'rgba(255, 255, 255, 0.8)' }} />
        <Typography variant="h6" sx={{ 
          color: 'white',
          fontWeight: 600,
          fontSize: '1rem'
        }}>
          Chat
        </Typography>
      </Box>
      
      {/* Messages */}
      <MessagesContainer>
        {chatMessages.length === 0 ? (
          <Box sx={{ 
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            textAlign: 'center',
            py: 3
          }}>
            <Typography variant="body2" sx={{ 
              color: 'rgba(255, 255, 255, 0.6)',
              fontStyle: 'italic'
            }}>
              No messages yet. Start the conversation!
            </Typography>
          </Box>
        ) : (
          chatMessages.map((msg, idx) => {
            const isOwn = msg.sender === 'You'; // You might need to adjust this logic
            return (
              <MessageBubble key={idx} isOwn={isOwn}>
                <MessageSender isOwn={isOwn}>
                  {msg.sender}
                </MessageSender>
                <MessageContent isOwn={isOwn}>
                  <Typography variant="body2" sx={{ 
                    fontSize: '0.875rem',
                    lineHeight: 1.4
                  }}>
                    {msg.body}
                  </Typography>
                </MessageContent>
              </MessageBubble>
            );
          })
        )}
      </MessagesContainer>
      
      {/* Input */}
      <InputContainer>
        <StyledTextField
          value={chatInput}
          onChange={e => setChatInput(e.target.value)}
          onKeyDown={handleChatKeyPress}
          placeholder="Type a message..."
          disabled={!isConnected}
          size="small"
          fullWidth
        />
        <SendButton 
          onClick={sendChatMessage} 
          disabled={!isConnected || !chatInput.trim()}
        >
          <Send sx={{ fontSize: '1.2rem' }} />
        </SendButton>
      </InputContainer>
    </ChatContainer>
  );
};

export default ChatInterface; 